import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { FiShoppingBag, FiHeart } from 'react-icons/fi';
import { AiFillHeart } from 'react-icons/ai';
import { useAuthStore } from '../store/useAuthStore';
import BlurUpImage from './BlurUpImage';
import { getThumbnail, getMediumImage } from '../utils/imageOptimization';
import '../styles/ProductCard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function ProductCard({ product, selectedCurrency = 'USD', convertPrice, currencySymbol = '$', isFeatured = false }) {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);

  const [images, setImages] = useState([]);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showCustomOrderModal, setShowCustomOrderModal] = useState(false);
  const [showQuickLookModal, setShowQuickLookModal] = useState(false);

  const checkWishlistStatus = useCallback(async () => {
    if (!API_BASE_URL || !token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/wishlists`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        console.warn(`Wishlist fetch returned ${response.status}: ${response.statusText}`);
        setIsWishlisted(false);
        return;
      }

      const wishlist = await response.json();
      const inWishlist = Array.isArray(wishlist) && wishlist.some((item) => item.product_id === product.id);
      setIsWishlisted(inWishlist);
    } catch (error) {
      console.error('Error checking wishlist:', error);
      setIsWishlisted(false);
    }
  }, [product.id, token]);

  // Fetch product images and wishlist status
  useEffect(() => {
    if (product.images && product.images.length > 0) {
      setImages(product.images);
    } else if (product.image) {
      setImages([{ image_url: product.image }]);
    }

    // Check wishlist status
    if (user && token) {
      checkWishlistStatus();
    }
  }, [product, user, token, checkWishlistStatus]);


  const getTotalStock = () => {
    if (!product.stockByColor) return 0;
    // Sum all quantities from all colors and sizes
    return Object.values(product.stockByColor).reduce((colorSum, colorStock) => {
      if (!colorStock || typeof colorStock !== 'object') return colorSum;
      return colorSum + Object.values(colorStock).reduce((sum, qty) => sum + (qty || 0), 0);
    }, 0);
  };

  const getAvailableSizes = () => {
    if (!product.stockByColor) return [];
    // Extract all unique sizes from all colors
    const sizeSet = new Set();
    Object.values(product.stockByColor).forEach(colorStock => {
      if (colorStock && typeof colorStock === 'object') {
        Object.keys(colorStock).forEach(size => sizeSet.add(size));
      }
    });
    return Array.from(sizeSet).sort((a, b) => parseFloat(a) - parseFloat(b));
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    // Redirect to product detail page to select size and add to cart
    navigate(`/product/${product.id}`);
  };

  const handleCustomOrder = async (quantity, size) => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!size) {
      alert('Please select a size');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/custom-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          productId: product.id,
          size: size,
          quantity: parseInt(quantity),
        }),
      });

      if (response.ok) {
        alert('Custom order submitted! We will contact you soon to confirm.');
        setShowCustomOrderModal(false);
      } else {
        const data = await response.json();
        alert(`Failed to create custom order: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating custom order:', error);
      alert('Error submitting custom order');
    }
  };

  const handleViewDetails = () => {
    navigate(`/product/${product.id}`);
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (isWishlisted) {
        const response = await fetch(`${API_BASE_URL}/api/wishlists/${product.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          setIsWishlisted(false);
        }
      } else {
        const response = await fetch(`${API_BASE_URL}/api/wishlists`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ productId: product.id }),
        });
        if (response.ok) {
          setIsWishlisted(true);
        }
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
    }
  };

  const currentImage = images.length > 0 ? images[0] : { image_url: product.image };

  return (
    <div className={`product-card ${isFeatured ? 'featured-product-card' : ''}`}>
      <div
        className="product-image-link"
        onClick={handleViewDetails}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === 'Enter' && handleViewDetails()}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div className="product-image">
          {images.length > 0 && (
            <>
              <BlurUpImage
                src={getThumbnail(currentImage.image_url || currentImage.image)}
                alt={product.name}
                className="product-image-primary"
                objectFit="cover"
                objectPosition="center"
                lazy={true}
              />
              {images.length > 1 && isHovering && (
                <BlurUpImage
                  src={getThumbnail(images[1].image_url || images[1].image)}
                  alt={`${product.name} secondary`}
                  className="product-image-secondary"
                  objectFit="cover"
                  objectPosition="center"
                  lazy={false}
                />
              )}
              {isHovering && (
                <div className="hover-quick-look-overlay">
                  <span>Quick Look</span>
                </div>
              )}
            </>
          )}
          <div className="product-overlay">
            <button className="btn-quick-look" onClick={(e) => {
              e.stopPropagation();
              setShowQuickLookModal(true);
            }}>
              Quick Look
            </button>
          </div>
        </div>
      </div>

      <div className="product-info">
        <div className="product-name-link" onClick={handleViewDetails} role="button" tabIndex={0} onKeyPress={(e) => e.key === 'Enter' && handleViewDetails()}>
          <h3 className="product-name">{product.name}</h3>
        </div>

        <div className="product-footer">
          <div className="product-price">
            {product.discount_price && parseFloat(product.discount_price) > 0 ? (
              <>
                <span className="original-price">
                  {currencySymbol}
                  {convertPrice ? convertPrice(product.price) : parseFloat(product.price).toFixed(2)}
                </span>
                <span className="discounted-price">
                  {currencySymbol}
                  {convertPrice ? convertPrice(product.discount_price) : parseFloat(product.discount_price).toFixed(2)}
                </span>
              </>
            ) : (
              <>
                {currencySymbol}
                {convertPrice ? convertPrice(product.price) : parseFloat(product.price).toFixed(2)}
              </>
            )}
          </div>
          <div className="product-actions">
            <button
              className="btn-add-cart"
              onClick={handleAddToCart}
              title="View details to select size and add to cart"
            >
              <FiShoppingBag />
            </button>
            <button
              className={`btn-wishlist ${isWishlisted ? 'wishlisted' : ''}`}
              onClick={handleWishlist}
              title={user ? (isWishlisted ? "Remove from wishlist" : "Add to wishlist") : "Sign in to add to wishlist"}
            >
              {isWishlisted ? <AiFillHeart /> : <FiHeart />}
            </button>
          </div>
        </div>

        {/* Custom Order Modal */}
        {showCustomOrderModal && (
          <div className="custom-order-modal-overlay" onClick={() => setShowCustomOrderModal(false)}>
            <div className="custom-order-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Request Custom Order</h3>
              <p>{product.name}</p>
              <div className="custom-order-form">
                <label>Size (EU):</label>
                <select id="custom-size" defaultValue="">
                  <option value="">Select a size</option>
                  {getAvailableSizes().map((size) => (
                    <option key={size} value={size}>
                      EU {size}
                    </option>
                  ))}
                </select>
                <label>Quantity:</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  defaultValue="1"
                  id="custom-qty"
                />
              </div>
              <p className="custom-order-note">We will contact you to confirm delivery time and finalize details.</p>
              <div className="custom-order-actions">
                <button
                  className="btn-submit-order"
                  onClick={() => handleCustomOrder(document.getElementById('custom-qty').value, document.getElementById('custom-size').value)}
                >
                  Submit Order Request
                </button>
                <button
                  className="btn-cancel-order"
                  onClick={() => setShowCustomOrderModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Look Modal */}
        {showQuickLookModal && createPortal(
          <div className="quick-look-modal-overlay" onClick={() => setShowQuickLookModal(false)}>
            <div className="quick-look-modal" onClick={(e) => e.stopPropagation()}>
              <button
                className="quick-look-close-btn"
                onClick={() => setShowQuickLookModal(false)}
                title="Close"
              >
                ×
              </button>
              <div className="quick-look-content">
                <div className="quick-look-images">
                  {images.length > 0 && (
                    <BlurUpImage
                      src={getMediumImage(currentImage.image_url || currentImage.image)}
                      alt={product.name}
                      className="quick-look-image"
                      objectFit="cover"
                      objectPosition="center"
                      lazy={false}
                    />
                  )}
                </div>
                <div className="quick-look-info">
                  <p className="quick-look-sku">{product.sku}</p>
                  <h2 className="quick-look-name">{product.name}</h2>
                  <p className="quick-look-category">{product.category}</p>

                  <div className="quick-look-price">
                    {product.discount_price && parseFloat(product.discount_price) > 0 ? (
                      <>
                        <span className="quick-look-original">
                          {currencySymbol}
                          {convertPrice ? convertPrice(product.price) : parseFloat(product.price).toFixed(2)}
                        </span>
                        <span className="quick-look-discounted">
                          {currencySymbol}
                          {convertPrice ? convertPrice(product.discount_price) : parseFloat(product.discount_price).toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <>
                        {currencySymbol}
                        {convertPrice ? convertPrice(product.price) : parseFloat(product.price).toFixed(2)}
                      </>
                    )}
                  </div>

                  {product.description && (
                    <p className="quick-look-description">{product.description}</p>
                  )}

                  <div className="quick-look-stock">
                    <span className="quick-look-in-stock">Available</span>
                    {getTotalStock() === 0 && (
                      <span className="quick-look-made-to-order">Made to order - May require additional time</span>
                    )}
                  </div>

                  <button
                    className="quick-look-view-details-btn"
                    onClick={() => {
                      setShowQuickLookModal(false);
                      handleViewDetails();
                    }}
                  >
                    View Full Details
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.getElementById('modal-root')
        )}
      </div>
    </div>
  );
}
